import { useMemo } from 'react'

import { BrokenImageOutlined } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { ImageObject } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { addSeconds, format, formatDistance, fromUnixTime } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { NoSrcPlaceholderWrapper, Thumbnail } from 'domains/Workspaces/Events'
import { StateVisualizer, SortableHeader } from 'domains/Workspaces/Images/'
import { formatDuration } from 'utils'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'entity', 'domain'])

  return useMemo<ColumnDef<ImageObject>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name.length ? row.name : row.id }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const { src, alt } = getValue<{ src?: string; alt: string }>()
          if (src) {
            return (
              <picture>
                <Thumbnail type="image/jpeg" data={src} aria-label={alt}>
                  <NoSrcPlaceholderWrapper>
                    <BrokenImageOutlined />
                  </NoSrcPlaceholderWrapper>
                </Thumbnail>
              </picture>
            )
          }
          return (
            <NoSrcPlaceholderWrapper>
              <BrokenImageOutlined />
            </NoSrcPlaceholderWrapper>
          )
        },
      },
      {
        accessorKey: 'name',
        header: t('name'),
        meta: {
          sizing: {
            px: 2,
            minWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) =>
          !!row.estimatedDuration && !!row.estimatedStartTime
            ? formatDistance(row.estimatedStartTime, addSeconds(row.estimatedStartTime, row.estimatedDuration))
            : '',
        header: t('domain:Workspace.Image.duration'),
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 300,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => (row.ended ? 'ended' : row.ended === null ? 'empty' : 'live'),
        id: '_status',
        header: () => t('status'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const state = cell.getValue<'ended' | 'live' | 'empty'>()
          const label = t(state)

          return (
            <StateVisualizer state={state === 'live'} variant="text-sm">
              {label}
            </StateVisualizer>
          )
        },
      },
      {
        accessorKey: 'creationTime',
        header: () => (
          <SortableHeader title={t('domain:Workspace.Image.creationTime')} sortProp="orderByCreationTime" />
        ),
        meta: {
          sizing: {
            minWidth: 180,
            px: 1,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {format(new Date(getValue<string>()), DATE_TIME_FORMAT)}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => ({
          deleteAfter: row.activeLifecyclePolicy.deleteAfter,
          creationTimestamp: row.creationTimestamp,
        }),
        header: t('domain:Workspace.Image.lifecycle'),
        meta: {
          sizing: {
            width: 280,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const values = getValue<{ deleteAfter: number | undefined; creationTimestamp: number }>()
          const seconds = values.deleteAfter ? parseInt(values.deleteAfter.toFixed(0)) : null

          const label = seconds
            ? formatDuration(
                seconds,
                { seconds: true, minutes: true, hours: true, days: true, years: true },
                { suffixed: true, longSuffix: true, omitEmptyValues: true },
              )[0]
            : t('domain:Workspace.Image.lifecycleNoExpiry')

          const tooltipTitle = seconds
            ? t('domain:Workspace.Image.lifecycleDeletion', {
                time: (
                  formatDuration(
                    seconds,
                    { seconds: true, minutes: true, hours: true, days: true, years: true },
                    { suffixed: true, omitEmptyValues: true },
                  ) as string[]
                ).join(' '),
                deletionTime: format(fromUnixTime(values.creationTimestamp + seconds), DATE_TIME_FORMAT),
              })
            : t('domain:Workspace.Image.lifecycleNoDeletion')

          return (
            <Tooltip title={tooltipTitle} placement="top">
              <Typography>{label}</Typography>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [t],
  )
}
